import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BaseWrapper from '../../components/shared/BaseWrapper';
import { postInvoiceItem, getContractItem } from '../API/billingAPI';
import { getClient } from '../API/clientAPI';
import DeleteIcon from "@mui/icons-material/Delete";
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import invoiceValidationSchema from "../../components/Billing/invoiceValidationSchema";
import { dateOnlyToDate, formatDateInPhilippines, todayInPhilippines } from "./utils/phDate";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";

import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  CardHeader,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

const formatDate = formatDateInPhilippines;

const formatDateForInput = (dateObj) => {
  return formatDate(dateObj) || "";
};

const normalizeDateOnly = (value) => {
  if (!value) return null;
  const date = dateOnlyToDate(formatDateInPhilippines(value));
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const getInvoiceStatus = (billingDate, dueDate) => {
  const billing = normalizeDateOnly(billingDate);
  const due = normalizeDateOnly(dueDate);

  if (billing && due && billing > due) return "INVALID";
  if (!due) return "OPEN";

  const today = normalizeDateOnly(todayInPhilippines());
  return today > due ? "OVERDUE" : "OPEN";
};

const getInvoiceStatusHelperText = (status) => {
  if (status === "INVALID") {
    return "Billing date cannot be after due date. Change the billing date or due date before creating the invoice.";
  }

  if (status === "OVERDUE") {
    return "This invoice will be created as OVERDUE because today is after the due date.";
  }

  return "This invoice will be created as OPEN.";
};

function RichTextDescriptionEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  return (
    <Box>
      <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
        <Button
          size="small"
          variant={editor.isActive("bold") ? "contained" : "outlined"}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </Button>

        <Button
          size="small"
          variant={editor.isActive("italic") ? "contained" : "outlined"}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </Button>

        <Button
          size="small"
          variant={editor.isActive("underline") ? "contained" : "outlined"}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          U
        </Button>
      </Box>

      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: 1,
          p: 1,
          minHeight: 90,
          "& .ProseMirror": {
            outline: "none",
            minHeight: 70,
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}

function CreateInvoice() {
  const CURRENCY = 'PHP';
  const navigate = useNavigate();
  const location = useLocation();
  const selectedClient = location.state?.selectedClient;
  const selectedContract = location.state?.selectedContract;
  const source = location.state?.source || 'manageContracts';
  const [ activeContract, setActiveContract ] = useState(selectedContract ?? []);
  const [ contracts, setContracts ] = useState([]);
  const [ contractsLoading, setContractsLoading ] = useState(false);

  // for creating invoice payload
  const createEmptyInvoice = () => {
    return {
      invoice_number: "",
      contract_id: selectedContract?.contract_id || selectedContract?.id || "",
      client: {
        id: selectedClient?.id || "",
        name: selectedClient?.name || "",
        billing_address: selectedClient?.billing_address || "",
      },
      billing_date: null,
      due_date: null,
      line_items: [],
      tax_rate_bps: 1200,
      status: "OPEN"
    };
  };
  const [ errors, setErrors ] = useState({});
  const [ loading, setLoading ] = useState(false);
  const [ successMessage, setSuccessMessage ] = useState("");
  const [ form, setForm ] = useState(createEmptyInvoice());
  const [ clientLoading, setClientLoading ] = useState(false);
  const [ overrideDialogOpen, setOverrideDialogOpen ] = useState(false);
  const [ pendingSubmitData, setPendingSubmitData ] = useState(null);
  const [ overrideWarnings, setOverrideWarnings ] = useState([]);

  const formatAddress = (addressObj) => {
    if (!addressObj) return "";
    const parts = [
      addressObj.house_unit_street ?? "",
      addressObj.barangay ?? "",
      addressObj.city ?? "",
      addressObj.metro_province ?? "",
      addressObj.zipcode ?? "",
    ];
    return parts.join(", ");
  }

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (selectedClient?.id) {
        setClientLoading(true);
        try {
          const clientData = await getClient(selectedClient.id);
          if (clientData) {
            let billingAddress = "";
            if (clientData.addresses && Array.isArray(clientData.addresses)) {
              const addr = clientData.addresses[0]
              billingAddress = formatAddress(addr);
            }
            else if (clientData.address) {
              billingAddress = formatAddress(clientData.address);
            }
            handleClientFieldChange('billing_address', billingAddress);
          }
        } catch (error) {
          console.error('Error fetching client details:', error);
        } finally {
          setClientLoading(false);
        }
      }
    };

    fetchClientDetails();
  }, [selectedClient?.id]);

  useEffect(() => {
    const fetchContracts = async () => {
      if (!selectedClient?.id) return;
      setContractsLoading(true);
      try {
        const data = await getContractItem(selectedClient.id);
        const list = data?.contracts ?? (Array.isArray(data) ? data : []);
        setContracts(list);
        if (selectedContract && list.length > 0) {
          const match = list.find(
            c => (c.contract_id || c.id) === (selectedContract.contract_id || selectedContract.id)
          );
          if (match) setActiveContract(match);
        }
      } catch {
        setContracts([]);
      } finally {
        setContractsLoading(false);
      }
    };
    fetchContracts();
  }, [selectedClient?.id]);

  const handleCancelClick = () => {
    if (source === "viewContract") {
      navigate(`/view-contract/${selectedContract?.contract_id || selectedContract?.id}`, {
        state: {
          contract: selectedContract,
          selectedClient,
          source: "viewContract",
        }
      });
    } else {
      navigate("/manage-contracts", { state: { selectedClient } });
    }
  };

  const handleFieldChange = (field, value) => {
    setForm({ ...form, [field]: value });

    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleClientFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      client: {
        ...prev.client,
        [field]: value
      }
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedLineItems = [...form.line_items];
    updatedLineItems[index] = {
      ...updatedLineItems[index],
      [field]: field === 'amount_minor' ? Number(value) : value
    };
    setForm({ ...form, line_items: updatedLineItems });
  };

  const handleLineItemDescriptionFormatChange = (index, formatKey) => {
    const updatedLineItems = [...form.line_items];
    const currentFormat = updatedLineItems[index].description_format || {
      bold: false,
      italic: false,
      underline: false,
    };

    updatedLineItems[index] = {
      ...updatedLineItems[index],
      description_format: {
        ...currentFormat,
        [formatKey]: !currentFormat[formatKey],
      },
    };

    setForm({ ...form, line_items: updatedLineItems });
  };

  const handleAmountInput = (index, e) => {
    const digits = e.target.value.replace(/\D/g, '');
    handleLineItemChange(index, 'amount_minor', parseInt(digits || '0', 10 ));
  };

  const handleTaxRateInput = (e) => {
    const digits = e.target.value.replace(/\D/g, "");

    handleFieldChange(
      "tax_rate_bps",
      digits === "" ? "" : parseInt(digits, 10)
    );
  };

  const addLineItem = () => {
    const newId = `LI_${form.line_items.length + 1}`;
    setForm({
      ...form,
      line_items: [
        ...form.line_items,
        {
          id: newId,
          sale_date: "",
          description: "",
          description_format: {
            bold: false,
            italic: false,
            underline: false,
          },
          amount_minor: 0,
        },
      ]
    });
  };

  const removeLineItem = (index) => {
    setForm({
      ...form,
      line_items: form.line_items.filter((_, i) => i !== index)
    });
  };

  const calculateTotals = () => {
    const netTotal = form.line_items.reduce((sum, item) => sum + (item.amount_minor || 0), 0);
    const taxAmount = Math.round(netTotal * (form.tax_rate_bps / 10000));
    return { netTotal, taxAmount, totalDue: netTotal + taxAmount };
  };

  const doCreateInvoice = async (submitData) => {
    setLoading(true);

    try {
      console.log("submitData: ", submitData);

      const res = await postInvoiceItem(
        selectedClient?.id,
        activeContract?.contract_id || activeContract?.id,
        submitData
      );

      if (!res) {
        setErrors({
          form: "Failed to create invoice. Please check the invoice details and try again.",
        });
        return;
      }

      setSuccessMessage("Invoice created successfully!");

      setTimeout(() => {
        handleCancelClick();
      }, 1500);
    } catch (err) {
      setErrors({
        form: err.message || "Failed to create invoice. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const computedStatus = getInvoiceStatus(form.billing_date, form.due_date);

      if (computedStatus === "INVALID") {
        setLoading(false);
        setErrors({
          status: "Invalid invoice dates.",
          due_date: "Due date must be after or equal to billing date.",
        });
        return;
      }

      if (form.contract_id) {
        try {
          const contractData = await getContractItem(selectedClient?.id);
          const contractExists = contractData.contracts?.some(
            contract => (contract.contract_id || contract.id) === form.contract_id
          );

          if (!contractExists) {
            setLoading(false);
            setErrors({ contract_id: "Contract not found. Please select a valid contract." });
            return;
          }
        } catch (contractError) {
          setLoading(false);
          console.error("Error validating contract:", contractError);
          setErrors({ contract_id: "Unable to validate contract. Please try again." });
          return;
        }
      }

      const formToValidate = {
        ...form,
        status: computedStatus,
      };

      await invoiceValidationSchema.validate(formToValidate, { abortEarly: false });

      const stored = localStorage.getItem('user');
      const storedUser = stored ? JSON.parse(stored) : null;
      const userInfo = storedUser 
        ? {
            user_id: storedUser.username || storedUser.user_ud || '',
            name: `${storedUser.firstname || ''} ${storedUser.lastname || ''}`.trim()
          }
        : { user_id: 'system', name: 'system' };

      const { netTotal, taxAmount, totalDue } = calculateTotals();

      const submitData = {
        invoice_number: form.invoice_number.trim(),
        contract_id: form.contract_id,
        client: {
          id: form.client.id,
          name: form.client.name.trim(),
          billing_address: form.client.billing_address.trim(),
        },
        billing_date: formatDate(form.billing_date),
        due_date: formatDate(form.due_date),
        payment_date: null,
        line_items: form.line_items,
        net_total_minor: netTotal,
        tax_rate_bps: form.tax_rate_bps,
        tax_minor: taxAmount,
        total_due_minor: totalDue,
        status: computedStatus,
        user_id: userInfo.user_id,
        user_name: userInfo.name,
      };

      const warnings = [];

      const contractPrice = activeContract.price_minor ?? null;
      if (contractPrice !== null) {
        const contractPriceMinor = Number(contractPrice);
        if (totalDue > contractPriceMinor) {
          const fmt = (v) => (v / 100).toFixed(2);
          warnings.push(
            `This invoice total (${fmt(totalDue)}) exceeds the contract price of ${fmt(contractPriceMinor)}.`
          );
        }
      }

      if (warnings.length > 0) {
        setOverrideWarnings(warnings);
        setPendingSubmitData(submitData);
        setOverrideDialogOpen(true);
        setLoading(false);
        return;
      }

      await doCreateInvoice(submitData);
    } catch (err) {
      setLoading(false);
      if (err.inner) {
        const map = {};
        err.inner.forEach((e) => {
          map[e.path] = e.message;
        });
        setErrors(map);
      } else {
        setErrors({ form: err.message || 'Validation error' });
      }
    }
  };

  const handleOverrideProceed = async () => {
    setOverrideDialogOpen(false);
    if (pendingSubmitData) {
      await doCreateInvoice(pendingSubmitData);
      setPendingSubmitData(null);
    }
  };

  const { netTotal, taxAmount, totalDue } = calculateTotals();
  const invoiceStatus = getInvoiceStatus(form.billing_date, form.due_date);

  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title='Billing'
      subTitle='Create Invoice'
      subTitleRoute='/create-invoice'
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
    >
      <Box component='form' onSubmit={handleSubmit} sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Invoice Details Header */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Invoice Details
                </Typography>
              }
              sx={{ pb: 0, pt: 0 }}
            />
          </Grid>
          {/* Invoice Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Invoice Number"
              value={form.invoice_number}
              onChange={(e) => handleFieldChange('invoice_number', e.target.value)}
              fullWidth
              error={!!errors.invoice_number}
              helperText={errors.invoice_number}
              placeholder="e.g., 1234567890"
            />
          </Grid>

          {/* Contract ID (Read-only IF creating from '/view-contract) */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={contracts}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option?.contract_id || option?.id || ''
              }
              isOptionEqualToValue={(option, value) =>
                (option?.contract_id || option?.id) === (value?.contract_id || value?.id)
              }
              value={activeContract}
              onChange={(e, value) => {
                setActiveContract(value);
                handleFieldChange('contract_id', value?.contract_id || value?.id || '');
              }}
              loading={contractsLoading}
              disabled={!!selectedContract}
              renderOption={(props, option) => (
                <li {...props} key={option.contract_id || option.id}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{option.contract_id || option.id}</span>
                    <small style={{ opacity: 0.7 }}>
                      Status: {option.status} | Start: {option.contract_start || 'N/A'}
                    </small>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contract ID"
                  fullWidth
                  error={!!errors.contract_id}
                  helperText={errors.contract_id}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {contractsLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Status"
              value={invoiceStatus}
              fullWidth
              disabled
              variant="outlined"
              error={invoiceStatus === "INVALID"}
              helperText={errors.status || getInvoiceStatusHelperText(invoiceStatus)}
            />
          </Grid>

          {/* Client Details header */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Client Information
                </Typography>
              }
              sx={{ pb: 0, pt: 0, mt: 2 }}
            />
          </Grid>

          {/* Client Name (Read-only) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client Name"
              value={form.client.name}
              onChange={(e) => handleClientFieldChange("name", e.target.value)}
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              error={!!errors["client.name"]}
              helperText={errors["client.name"]}
            />
          </Grid>

          {/* Client ID (Read-only) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client ID"
              value={form.client.id}
              fullWidth
              disabled
              variant="outlined"
            />
          </Grid>

          {/* Billing Address */}
          <Grid item xs={12}>
            <TextField
              label="Billing Address"
              value={form.client.billing_address}
              onChange={(e) => handleClientFieldChange('billing_address', e.target.value)}
              fullWidth
              multiline
              minRows={3}
              error={!!errors['client.billing_address']}
              helperText={errors['client.billing_address']}
              disabled={clientLoading}
            />
          </Grid>


          {/* Dates Header */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Invoices Dates
                </Typography>
              }
              sx={{ pb: 0, pt: 0, mt: 2 }}
            />
          </Grid>

          {/* Billing Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label='Billing Date'
                value={form.billing_date}
                onChange={(date) => handleFieldChange("billing_date", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.billing_date,
                    helperText: errors.billing_date
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Due Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Due Date"
                value={form.due_date}
                onChange={(date) => handleFieldChange("due_date", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.due_date,
                    helperText: errors.due_date
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Line Items Header */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Line Items
                </Typography>
              }
              sx={{ pb: 0, pt: 0, mt: 2 }}
            />
          </Grid>
          {/* Line Items table - Only show if line items exist */}
          {form.line_items.length > 0 && (
            <>
              <Grid item xs={12}>
                <Table
                  sx={{
                    borderCollapse: "separate",
                    borderSpacing: "0 12px",
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ width: 180 }}>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right" sx={{ width: 190 }}>Amount</TableCell>
                      <TableCell align="center" sx={{ width: 90 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.line_items.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: "#fafafa",

                          "& td": {
                            py: 2,
                            px: 2,
                            verticalAlign: "top",
                            borderBottom: "1px solid #e5e7eb",
                          },

                          "& td:first-of-type": {
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                          },

                          "& td:last-of-type": {
                            borderTopRightRadius: 8,
                            borderBottomRightRadius: 8,
                          },
                        }}
                      >
                        <TableCell sx={{ width: 190 }}>
                          <Box sx={{ pt: "40px" }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                label="Date"
                                value={dateOnlyToDate(item.sale_date)}
                                onChange={(date) =>
                                  handleLineItemChange(index, "sale_date", formatDateForInput(date))
                                }
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ minWidth: 420 }}>
                            <RichTextDescriptionEditor
                              value={item.description}
                              onChange={(html) =>
                                handleLineItemChange(index, "description", html)
                              }
                            />
                          </Box>
                        </TableCell>

                        <TableCell align="right" sx={{ width: 190 }}>
                          <Box sx={{ pt: "40px", display: "flex", justifyContent: "flex-end" }}>
                            <TextField
                              size="small"
                              type="text"
                              value={(item.amount_minor / 100).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              onChange={(e) => handleAmountInput(index, e)}
                              inputProps={{ inputMode: "numeric" }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">{CURRENCY}</InputAdornment>
                                ),
                              }}
                              sx={{ width: 170 }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell align="center" sx={{ width: 80 }}>
                          <Box sx={{ pt: "40px" }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeLineItem(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            </>
          )}

          {/* Add Line Item Button */}
          <Grid item xs={12}>
            <Button
              variant='outlined'
              size='small'
              onClick={addLineItem}
            >
              Add Line Item
            </Button>
          </Grid>

          {/* Tax Rate */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Tax Rate'
              type='text'
              value={
                form.tax_rate_bps === "" ||
                form.tax_rate_bps === null ||
                form.tax_rate_bps === undefined
                  ? ""
                  : (form.tax_rate_bps / 100).toFixed(2)
              }
              onChange={handleTaxRateInput}
              inputProps={{ inputMode: 'numeric' }}
              InputProps={{
                endAdornment: <InputAdornment position='end'>%</InputAdornment>
              }}
              error={!!errors.tax_rate_bps}
              helperText={errors.tax_rate_bps || "e.g., 12.00 = 12%"}
              sx={{ mt: 1 }}
            />
          </Grid>        
        </Grid>

        {/* Totals Header */}
        <Grid item xs={12}>
          <CardHeader
            title={
              <Typography
                variant='h6'
                sx={{
                  paddingBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: '1rem',
                }}
              >
                Totals
              </Typography>
            }
            sx={{ pb: 0, pt: 2, mt: 2 }}
          />
        </Grid>

        {/* Total Summary */}
        <Grid item xs={12}>
          <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant='body2'> Net Total:</Typography>
                <Typography variant='h6'>{CURRENCY} {(netTotal / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant='body2'>Tax Amount:</Typography>
                <Typography variant='h6'>{CURRENCY} {(taxAmount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant='body2'>Total Due:</Typography>
                <Typography variant='h6' sx={{ fontWeight: 'bold' }}>{CURRENCY} {(totalDue / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Submit and Canel Buttons */}
        <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
          <Button
            variant='contained'
            type='submit'
            disabled={loading}
          >
            {loading && <CircularProgress size={20} />}
            {loading ? "Creating..." : "Create Invoice"}
          </Button>
          <Button
            sx={{ ml: 2 }}
            onClick={handleCancelClick}
            disabled={loading}
          >
            Cancel
          </Button>
        </Grid>
      </Box>
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)}>
        <DialogTitle>Invoice Warning</DialogTitle>
        <DialogContent>
          {overrideWarnings.map((msg, i) => (
              <DialogContentText key={i} sx={{ mb: overrideWarnings.length > 1 && i < overrideWarnings.length -1 ? 1.5 : 0 }}>
                {msg}
              </DialogContentText>
          ))}
          <DialogContentText sx={{ mt: 2 }}>
            Do you want to proceed anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleOverrideProceed} variant='contained' color='warning'>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </BaseWrapper>
  );
}

export default CreateInvoice;
